import { useContext, useEffect, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';

import { Deal } from '../../../gql/dealGql';
import {
  Notification,
  useDealUnreadMessageCountLazyQuery,
  useMarkRecipientThreadSeenMutation,
  useNotificationsSeenMutation,
  useOnMissedSipCallSubscription,
  useOnNewMessageSubscription,
} from '../../../gql/generated/graphql';

import ContactBox from './ContactBox';

import { RecipientTypeMap, isMessageUnseen } from '../../../constants/notifications';
import { useUser } from '../../../hooks/useUser';
import { logger } from '../../../libs/Logger';
import { TextWidgetContext } from '../../../libs/contextLib';
import { formatPhone } from '../../../libs/utils';
import { pluckDefined } from '../../../utils';
import { getBoxUnreadCount, getConversationBoxes, isActiveBox } from '../../../utils/messages';

type ContactPanelProps = {
  deal: Deal | null;
};

const isOrphanNotification = (notification: Notification): boolean =>
  !notification.customer_id && !notification.deal_contact_id && !notification.original_lessee_id;

const ContactPanel = ({ deal }: ContactPanelProps) => {
  const user = useUser();
  const { activeConversation, setActiveConversation } = useContext(TextWidgetContext);
  const [notificationsSeenMutation] = useNotificationsSeenMutation();
  const [markRecipientThreadSeen] = useMarkRecipientThreadSeenMutation();
  const [fetchUnreadCount, { data: unreadCountData }] = useDealUnreadMessageCountLazyQuery();
  const boxes = useMemo(() => (deal ? getConversationBoxes({ deal }) : []), [deal]);
  const refetchUnreadCount = (): void => {
    fetchUnreadCount({
      variables: { dealId: Number(deal?.id) },
      fetchPolicy: 'network-only',
      context: { queryDeduplication: false },
    });
  };
  useOnMissedSipCallSubscription({ skip: !deal?.id, onData: refetchUnreadCount });
  useOnNewMessageSubscription({
    variables: {
      dealPhoneNumbers: boxes.map(({ conversation }) => formatPhone(conversation.phone_number)),
    },
    skip: !deal?.id,
    onData: refetchUnreadCount,
  });

  useEffect(() => {
    const orphans = (deal?.notifications ?? []).filter(
      (notification) =>
        isOrphanNotification(notification) && isMessageUnseen(user.id, notification),
    );
    if (!deal?.id || !orphans.length) {
      return;
    }
    const markOrphansSeen = async (): Promise<void> => {
      try {
        await notificationsSeenMutation({
          variables: { notificationIds: pluckDefined(orphans, 'id'), seen: true },
        });
      } catch (e) {
        logger.error('ContactPanel.tsx', 'marking texts seen failed', null, e);
      }
    };
    markOrphansSeen();
  }, [user.id, deal?.id, deal?.notifications]);

  const refreshUnreadCounts = async (): Promise<void> => {
    if (!deal?.id) {
      return;
    }
    const dealId = Number(deal.id);
    const activeBox = boxes.find((box) => isActiveBox({ box, activeConversation }));
    if (activeBox?.personId != null) {
      try {
        await markRecipientThreadSeen({
          variables: {
            dealId,
            recipientType: RecipientTypeMap[activeBox.role],
            recipientId: activeBox.personId,
            phoneNumber: activeBox.conversation.phone_number,
          },
        });
      } catch (e) {
        logger.error('ContactPanel.tsx', 'marking thread seen failed', null, e);
      }
    }
    refetchUnreadCount();
  };

  useEffect(() => {
    refreshUnreadCounts();
  }, [deal?.id, activeConversation.role, activeConversation.phone_number]);

  return (
    <Flex w="100%" minH="60px" bgColor="white" mb={-2} pb={3} overflowX="auto">
      {boxes.map((box) => {
        const isActive = isActiveBox({ box, activeConversation });
        return (
          <ContactBox
            key={`${box.role}-${box.phoneType}`}
            box={box}
            handleClick={
              isActive ? refreshUnreadCounts : () => setActiveConversation(box.conversation)
            }
            isActive={isActive}
            unreadCount={getBoxUnreadCount({
              box,
              byRecipient: unreadCountData?.dealUnreadMessageCount.byRecipient ?? [],
            })}
          />
        );
      })}
    </Flex>
  );
};

export default ContactPanel;
